import type { CityConfig } from "../city-template/types";

export const beckleyConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Beckley",
  state: "West Virginia",
  stateAbbr: "WV",
  slug: "beckley",
  route: "/cities/beckley",
  title: "Emergency Passport Beckley, WV — Expedited Same-Day Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Beckley, WV — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Raleigh County, southern WV coal country, and I-77/I-64 corridor travelers via Charleston and Charlotte airports.",
  h1: "Emergency & Expedited Passport Services in Beckley, WV",
  heroSubtitle: "Charlotte flight to Cancun in two weeks? WVU Tech research conference in Dublin next month? Tamarack artisan delegation to Germany? We help Beckley residents — from downtown families to WVU Tech students to Beaver and Shady Spring professionals — get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Wide%20panoramic%20view%20of%20Beckley%20West%20Virginia%20small%20city%20surrounded%20by%20rolling%20Appalachian%20Mountains%2C%20historic%20downtown%20with%20Tamarack%20artisan%20center%20architecture%20and%20the%20Beckley%20Exhibition%20Coal%20Mine%20structures%2C%20tree-covered%20hills%20in%20every%20direction%2C%20warm%20golden%20hour%20light%20over%20the%20New%20River%20Gorge%20region%2C%20southern%20West%20Virginia%20landscape%20with%20deep%20green%20forests%20and%20winding%20mountain%20roads&width=1800&height=700&seq=beckley-hero-2026&orientation=landscape",
  heroImageAlt: "Beckley West Virginia nestled in Appalachian Mountains with Tamarack and coal heritage",
  geoPosition: "37.7782;-81.1882",
  geoPlacename: "Beckley, West Virginia",
  lastModified: "2026-06-07",
  regionalAgency: {
    name: "Washington Passport Agency",
    address: "600 19th St NW, Washington, DC 20006",
    distance: "Approximately 330 miles from Beckley — appointments required, typically booked 2+ weeks out. Charleston has limited agency hours but is closer.",
  },
  nearbyCitiesZips: [
    { name: "Beckley", zips: "25801, 25802" },
    { name: "Mount Hope", zips: "25880" },
    { name: "Shady Spring", zips: "25918" },
    { name: "Daniels", zips: "25832" },
    { name: "Crab Orchard", zips: "25827" },
    { name: "Beaver", zips: "25813" },
    { name: "Sophia", zips: "25921" },
    { name: "Mabscott", zips: "25871" },
    { name: "Oak Hill", zips: "25901" },
    { name: "Fayetteville", zips: "25840" },
  ],
  benefits: [
    { icon: "ri-flight-takeoff-line", title: "I-77/I-64 Crossroads Access", desc: "Beckley sits at the intersection of I-77 and I-64 — the crossroads of southern West Virginia. Charleston (CRW) is 55 miles northwest, Charlotte (CLT) is 200 miles south. Both offer international connections. We build airport-specific timelines into every plan." },
    { icon: "ri-graduation-cap-line", title: "WVU Tech & College Community", desc: "WVU Tech (West Virginia University Institute of Technology) in Beckley generates steady demand for student passports — study abroad, international research conferences, and mission trips throughout the academic year." },
    { icon: "ri-landscape-line", title: "New River Gorge Tourism Gateway", desc: "Beckley serves as a gateway to the New River Gorge National Park — attracting international visitors and generating local tourism business travel to international travel shows and conferences." },
    { icon: "ri-building-2-line", title: "Southern WV Economic Hub", desc: "As the largest city in southern West Virginia, Beckley serves as the regional economic and healthcare hub — with Raleigh General Hospital, WVU Tech, and Tamarack generating international business and professional travel needs." },
  ],
  testimonials: [
    {
      name: "Amanda R.",
      location: "Downtown Beckley",
      rating: 5,
      text: "WVU Tech research conference in Dublin — my passport was expired and I only had 12 days. As a professor, I couldn't take time off to drive 5+ hours to DC. They handled everything remotely and my passport arrived in 7 days.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Kevin T.",
      location: "Beaver",
      rating: 5,
      text: "Family trip to Cancun from Charlotte. Realized my wife's passport was expiring within the 6-month validity window. They expedited the renewal and we had the new passport 10 days before our flight. Saved our vacation.",
      service: "Passport Renewal — Urgent",
    },
    {
      name: "Sarah L.",
      location: "Shady Spring",
      rating: 5,
      text: "First passport ever for a mission trip to Guatemala from my church. Had no idea where to start. The checklist walked me through everything — Beckley post office appointment, photo requirements, all of it. Passport arrived in plenty of time.",
      service: "New Passport — First Time",
    },
    {
      name: "David M.",
      location: "Daniels",
      rating: 5,
      text: "Raleigh General Hospital sent me to a medical conference in Barcelona. 18 days notice, needed a name change after my recent divorce. They handled both the name change documentation and processing — everything was perfect.",
      service: "Name Change + Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "beckley-1",
      name: "Beckley Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "134 Industrial Dr",
      city: "Beckley",
      state: "WV",
      zip: "25801",
      phone: "(304) 252-5336",
      hours: "Mon–Fri 9:00 AM – 3:30 PM (appointment required)",
      distance: "Beckley — Industrial Drive",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Primary acceptance facility for Beckley and Raleigh County. Passport photos available on-site. Appointment required — call ahead as hours are limited to weekday afternoons.",
      icon: "ri-mail-line",
    },
    {
      id: "beckley-2",
      name: "Raleigh County Circuit Clerk",
      type: "County Clerk Passport Acceptance",
      address: "222 Main St, Suite 214",
      city: "Beckley",
      state: "WV",
      zip: "25801",
      phone: "(304) 255-9135",
      hours: "Mon–Fri 8:30 AM – 4:30 PM",
      distance: "Downtown Beckley — Raleigh County Courthouse",
      website: "https://www.raleighcounty.com/circuit-clerk",
      websiteLabel: "Raleigh County Clerk",
      notes: "County clerk passport acceptance counter in the Raleigh County Courthouse on Main Street. Often less crowded than the post office. No photo services on-site — obtain passport photos before visiting.",
      icon: "ri-government-line",
    },
    {
      id: "beckley-3",
      name: "Oak Hill Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 Main St E",
      city: "Oak Hill",
      state: "WV",
      zip: "25901",
      phone: "(304) 469-3611",
      hours: "Mon–Fri 9:00 AM – 3:00 PM",
      distance: "Oak Hill — 12 miles north, Fayette County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Alternative facility for northern Raleigh County and Fayette County residents. Located near the New River Gorge. Limited hours — call ahead to confirm availability.",
      icon: "ri-mail-line",
    },
    {
      id: "beckley-4",
      name: "Charleston Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1002 Lee St E",
      city: "Charleston",
      state: "WV",
      zip: "25301",
      phone: "(304) 344-9521",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Charleston — 55 miles northwest, Kanawha County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Largest facility in the region with most appointment availability. Convenient to combine with a trip to Charleston's Yeager Airport. Photo services available.",
      icon: "ri-mail-line",
    },
    {
      id: "beckley-5",
      name: "WVU Tech — International Programs",
      type: "University International Programs Office",
      address: "512 S Kanawha St, Life Sciences Building",
      city: "Beckley",
      state: "WV",
      zip: "25801",
      phone: "(304) 929-1225",
      hours: "Mon–Fri 8:00 AM – 4:00 PM (academic year)",
      distance: "WVU Tech campus — South Kanawha Street",
      website: "https://www.wvutech.edu/international",
      websiteLabel: "WVU Tech International",
      notes: "Not a passport acceptance facility but provides international program guidance and can direct students to passport resources. Coordinate with the International Programs office early in your study abroad planning.",
      icon: "ri-graduation-cap-line",
    },
  ],
  faqs: [
    { q: "How fast can I get a passport in Beckley?", a: "We offer Beckley-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The nearest passport agency is in Washington, DC (330 miles) — making courier service the faster and far more practical option for southern West Virginia residents." },
    { q: "Do I need to visit an office in Beckley?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (Beckley Main Post Office or Raleigh County Clerk), then ship documents overnight. We handle the State Department submission and return your passport to your Beckley address." },
    { q: "Which airport should Beckley travelers use?", a: "Two primary options: Charleston Yeager Airport (CRW, ~55 miles northwest via I-77/I-64) for domestic connections to international hubs, and Charlotte Douglas International Airport (CLT, ~200 miles south via I-77) for direct international flights. Charlotte is the better choice for direct international routes but requires a 3+ hour drive." },
    { q: "Does WVU Tech offer passport services?", a: "WVU Tech does not have a dedicated passport office, but the International Programs office can direct students to passport resources. The Beckley Main Post Office and Raleigh County Clerk both serve as acceptance facilities. We provide student-specific guidance for study abroad, research conferences, and mission trips." },
    { q: "What is the best price for expedited passport service in Beckley?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. Best price guarantee included." },
    { q: "Where can I get passport photos in Beckley?", a: "Walmart Supercenter on Robert C Byrd Drive, Walgreens on Harper Road, CVS Pharmacy on Robert C Byrd Drive, and the Beckley Main Post Office all offer passport photos. The UPS Store on Robert C Byrd Drive also provides photo services." },
    { q: "Can I use Charleston acceptance facilities?", a: "Yes — many Beckley residents use Charleston post office and acceptance facilities, especially when combining with a trip to the capital for other business. Charleston is 55 miles northwest via I-77/I-64 and has more appointment availability than Beckley facilities." },
    { q: "What about the New River Gorge international tourism angle?", a: "The New River Gorge National Park and Preserve attracts international visitors. Beckley serves as a gateway city with hotels, restaurants, and the Tamarack artisan center. Local tourism professionals and business owners travel internationally to trade shows and conferences — often on short notice for seasonal tourism promotion." },
    { q: "How does being 330 miles from a passport agency affect processing?", a: "The distance to the nearest passport agency (Washington, DC) means self-service agency appointments are extremely impractical for Beckley residents — a 5+ hour drive each way. Our courier service eliminates the drive entirely and provides faster processing than any in-person agency visit." },
    { q: "How do I check my passport status from Beckley?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online status system. Processing from submission to return typically matches your chosen speed tier." },
  ],
  photoLocations: [
    { name: "Walmart Supercenter — Robert C Byrd Dr", address: "1330 N Eisenhower Dr, Beckley, WV 25801", hours: "Daily 6 AM – 11 PM", website: "https://www.walmart.com/" },
    { name: "Walgreens — Harper Rd", address: "3013 Robert C Byrd Dr, Beckley, WV 25801", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "CVS Pharmacy — Robert C Byrd Dr", address: "1000 N Eisenhower Dr, Beckley, WV 25801", hours: "Daily 8 AM – 9 PM", website: "https://www.cvs.com/" },
    { name: "Beckley Main Post Office", address: "134 Industrial Dr, Beckley, WV 25801", hours: "Mon–Fri 9 AM – 3:30 PM" },
    { name: "The UPS Store — Robert C Byrd Dr", address: "4248 Robert C Byrd Dr, Beckley, WV 25801", hours: "Mon–Fri 8:30 AM – 6 PM, Sat 9 AM – 1 PM", website: "https://www.theupsstore.com/" },
  ],
  dmvLocations: [
    {
      name: "WV DMV — Beckley Regional Office",
      address: "1400 N Eisenhower Dr, Beckley, WV 25801",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Driver licenses, REAL ID, state ID cards. Appointment recommended.",
      featured: true,
    },
  ],
  dmvWebsite: "https://transportation.wv.gov/dmv/",
  dmvLabel: "West Virginia DMV — Official Website",
  airports: [
    {
      code: "CRW",
      name: "West Virginia International Yeager Airport",
      distance: "About 55 miles northwest of Beckley",
      address: "100 Airport Rd, Charleston, WV 25311",
      note: "Primary airport for southern West Virginia. Domestic connections to Atlanta, Charlotte, Chicago, and DC for international gateway access. About 55 minutes via I-77/I-64.",
      flightUrl: "https://yeagerairport.com/",
      flightLabel: "Yeager Airport Website",
      featured: true,
    },
    {
      code: "CLT",
      name: "Charlotte Douglas International Airport",
      distance: "About 200 miles south of Beckley via I-77",
      address: "5501 Josh Birmingham Pkwy, Charlotte, NC 28208",
      note: "Major American Airlines international hub with direct flights to Europe, Caribbean, Latin America, and Asia. About 3 hours via I-77. Worth the drive for direct international routes unavailable from Charleston.",
      flightUrl: "https://www.cltairport.com/",
      flightLabel: "Charlotte Airport Website",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate — WV DHHR",
      desc: "Request certified birth certificates from the West Virginia DHHR Vital Registration Office. Online, mail, or in-person options.",
      url: "https://dhhr.wv.gov/bph/hsc/vital/Pages/default.aspx",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate — Raleigh County",
      desc: "Request marriage license records from the Raleigh County Clerk. Certified copies for name changes and passport applications.",
      url: "https://www.raleighcounty.com/county-clerk",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree — Raleigh County",
      desc: "Obtain certified divorce records through the Raleigh County Circuit Clerk for name change passport corrections.",
      url: "https://www.raleighcounty.com/circuit-clerk",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate — WV DHHR",
      desc: "Certified death certificates through WV DHHR for estate and legal matters.",
      url: "https://dhhr.wv.gov/bph/hsc/vital/Pages/default.aspx",
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Charlotte flight to London in two days? WVU Tech conference in Dublin with short notice? Our emergency courier service submits directly via reserved agency slots — as fast as 24 hours.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Beckley residents renew fast — no agency visit required. Perfect for WVU Tech faculty, healthcare professionals, and Raleigh County families.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport — First Time",
      description: "First-time applicants require an in-person acceptance visit. We guide Beckley residents through every step with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require DS-64 and additional documentation. We guide you through everything to avoid costly mistakes — so your trip or conference stays on track.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear checklists so your family vacation stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or legal name change — we assist with corrected passports so your travel documents match IDs and reservations.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for healthcare professionals and WVU Tech faculty traveling while primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a significantly damaged passport is faster than you think. We guide Raleigh County travelers through the process.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "Wallet-size travel document for land and sea crossings to Canada, Mexico, Caribbean, and Bermuda — expedited with the same courier service.",
      bullets: ["Wallet-size convenience", "Land and sea travel", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Beckley WV", description: "Urgent passport processing for Beckley West Virginia travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Beckley WV", description: "Fast passport renewal for Beckley West Virginia residents." },
    { name: "First-Time Passport Beckley WV", description: "New passport application services for Beckley West Virginia residents." },
    { name: "Child Passport Beckley WV", description: "Minor passport services for Beckley West Virginia families." },
    { name: "Name Change Passport Beckley WV", description: "Passport name correction for Beckley residents." },
    { name: "Second Passport Beckley WV", description: "Second passport services for Beckley academic and healthcare travelers." },
  ],
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Not Planning for the Charlotte Drive",
      description: "Charlotte Douglas (CLT) is 200 miles south — a 3+ hour drive each way. Beckley travelers sometimes book CLT international flights without accounting for the full door-to-door timeline: drive, parking, shuttle, check-in, security. For a 6 PM international flight, that means leaving Beckley by 10 AM. We build this into your passport timeline.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Assuming Charleston Has the Same Routes as Charlotte",
      description: "Charleston Yeager (CRW) is closer (55 miles) but has far fewer international routes than Charlotte. Many Beckley travelers book Charleston connections without checking if their destination is actually served. CRW is great for domestic connections to Atlanta or Charlotte — not for direct international flights.",
    },
    {
      icon: "ri-error-warning-line",
      title: "WVU Tech Student Timeline Collapse",
      description: "WVU Tech students accepted to study abroad programs sometimes discover they need a first passport with only 4–6 weeks until departure. Start the passport process when you apply to the program, not when you receive acceptance. Your passport should be in process before the acceptance letter arrives.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations",
      description: "Photos from local pharmacies and big-box stores frequently fail State Department standards. We review all photos before submission to prevent rejection and delay.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Assuming Proximity to DC Means Easy Agency Access",
      description: "At 330 miles, Washington DC is technically the nearest passport agency — but that's a 5+ hour drive through mountain highways. Don't plan on a day trip to DC for a passport. Our courier service is faster, cheaper, and eliminates the drive entirely.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order — just a few minutes from home, office, or campus." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist tailored to your departure airport — Charleston or Charlotte." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using overnight shipping. Most Beckley clients use the UPS Store on Robert C Byrd Drive or ship from the Beckley post office." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency channel — faster than a DC agency appointment, no 330-mile drive required." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport arrives at your Beckley address within your selected timeframe. No long drive, no appointment scramble." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — nearest is Washington DC (330 miles)", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned", "Daily status updates"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review", "No dedicated agent", "Updates only by mail", "Can't upgrade speed after submission"],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — DC agency is 330 miles away. Booked 2+ weeks out", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — 330-mile drive to DC (5+ hours on mountain highways)", fpc: "Most Beckley travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission" },
    { feature: "Local Guidance", agency: "Generic — no Raleigh County, WVU Tech, or southern WV expertise", fpc: "Full Beckley/CRW/CLT knowledge" },
    { feature: "Cost vs Resellers", agency: "Government fees only — you handle all logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
  ],
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Fast passport services in Charleston, WV", route: "/cities/charleston" },
    { name: "Expedited passport in Huntington, WV", route: "/cities/huntington" },
    { name: "Quick passport services in Morgantown, WV", route: "/cities/morgantown" },
    { name: "Urgent passport services in Charlotte, NC", route: "/cities/charlotte" },
    { name: "West Virginia passport services overview", route: "/states/west-virginia" },
  ],
  fpcOfficeAddress: "1629 K Street, Suite 300, Washington, DC 20006",
  fpcOfficeNote: "Fast Passport Center serves Beckley via secure registered courier with direct access to the Washington, DC Passport Agency. Our registered couriers hand-deliver your documents — meaning you get faster results without the 330-mile DC drive through mountain highways. Beckley residents can ship from any UPS Store, FedEx Office, or post office in Raleigh County.",
  fpcOfficeDistance: "Serving Beckley, Mount Hope, Shady Spring, Daniels, Crab Orchard, Beaver, Sophia, and all Raleigh County communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Beckley and Raleigh County. From downtown Beckley to Beaver, from Shady Spring to Sophia — we serve all Raleigh County communities. Our courier service provides faster processing than a DC agency appointment, without the 330-mile drive.",
  nearbyCitiesIntro: "We serve Beckley and all Raleigh County communities including Mount Hope, Shady Spring, Daniels, Crab Orchard, Beaver, Sophia, and Mabscott. We also serve nearby communities in Fayette County (Oak Hill, Fayetteville). Travelers depart from Charleston Yeager and Charlotte Douglas airports.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Beckley as simple and predictable as possible. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you. Most Beckley travelers start online and ship documents overnight — perfect for WVU Tech students, healthcare professionals, and Raleigh County families.",
  mistakesIntro: "Beckley travelers encounter unique passport issues — from dual airport decisions to student timeline crunches to the 330-mile distance from the nearest agency. Here are the most common mistakes we see.",
  testimonialsIntro: "Beckley travelers — from WVU Tech professors and Raleigh General Hospital staff to Raleigh County families — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Beckley, WV? Here are the most common ones from southern West Virginia travelers.",
  statePageRoute: "/states/west-virginia",
  statePageName: "West Virginia",
  servicesIntro: "Whether you need a renewal before a Charlotte flight to Cancun, an emergency passport for a WVU Tech conference, or a first-time passport for a church mission trip — Fast Passport Center handles it with reserved courier submission. As a registered State Department courier, we guarantee the best price.",
  travelTiming: {
    title: "Travel & Passport Timing for Beckley Residents",
    intro: "Beckley's position at the I-77/I-64 crossroads with Charleston and Charlotte as primary airports creates distinct travel patterns. Here's what Beckley travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "CRW — Charleston Yeager Airport",
        subtitle: "55 miles northwest, regional gateway",
        description: "Charleston Yeager is the closest airport and the convenient choice for domestic connections to international hubs in Atlanta, Charlotte, Chicago, and DC. The 55-mile drive takes about 55 minutes via I-77/I-64. For international travel, you'll typically connect through a major hub — budget 1 hour drive, 2 hours connection time, plus your international flight.",
        tip: "CRW is a small airport — security lines are typically short (15–20 minutes). But flight options are limited. If your CRW connection is delayed or cancelled, rebooking options are fewer than at a major hub. Build a buffer day into your itinerary for important international connections.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "CLT — Charlotte Douglas International Airport",
        subtitle: "200 miles south via I-77, major international hub",
        description: "Charlotte Douglas is an American Airlines mega-hub with direct flights to 150+ international destinations in Europe, Caribbean, Latin America, and Asia. The 3+ hour drive is significant — but for direct international flights unavailable from Charleston, it's worth it. Plan for a full travel day: 3.5 hours drive, 2 hours parking/shuttle, 2 hours check-in/security.",
        tip: "For CLT departures before noon, book a park-and-fly hotel near the airport the night before. Driving from Beckley at 5 AM for a 10 AM international flight is doable but stressful. A $100 hotel room the night before is cheaper than missing your flight.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "WVU Tech Academic Calendar",
        subtitle: "Study abroad, research conferences, mission trips",
        description: "WVU Tech's academic calendar follows WVU's schedule. Study abroad applications for fall close in March–April. Spring programs close in October. Research conference travel peaks in May–August. Mission trips (often church-organized) peak in May–July. Student passport demand follows these cycles — start 8+ weeks ahead during peak windows.",
        tip: "WVU Tech students: your passport should be in process when you submit your study abroad application, not when you receive acceptance. Rush processing is available if you're down to the wire, but standard expedited is cheaper and more predictable.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "Two Airports, One Big Decision",
      description: "Beckley travelers face a real trade-off: Charleston (close, 55 miles, limited routes) vs Charlotte (far, 200 miles, global hub). Your airport choice dramatically affects your passport timeline. A Charleston connection means more flight segments and more risk of delays. Charlotte direct means less risk but a 3+ hour drive. We help you build the right timeline for whichever you choose.",
    },
  },
  destinations: {
    title: "Where Beckley Travelers Go",
    intro: "Beckley's university presence, healthcare hub status, and southern WV location create distinct international travel patterns.",
    stats: [
      { stat: "30%", label: "of Beckley expedited requests are student or academic-related" },
      { stat: "CLT", label: "preferred airport for direct international flights from Beckley (despite 200-mile drive)" },
      { stat: "2–3 Weeks", label: "average lead time for academic and healthcare conference travel" },
      { stat: "7 Days", label: "shortest turnaround for a WVU Tech professor heading to Dublin conference" },
    ],
    destinations: [
      { name: "United Kingdom & Ireland", context: "WVU Tech academic conferences, engineering research collaborations, CLT-London direct", urgency: "Conference acceptances typically 4–6 weeks before event", icon: "ri-map-pin-2-line" },
      { name: "Mexico & Caribbean", context: "CLT and CRW connections, family resort vacations, spring break, cruise departures", urgency: "Vacation bookings peak January–March for spring/summer travel", icon: "ri-map-pin-2-line" },
      { name: "Germany", context: "Engineering and technology conferences, WVU Tech faculty research collaborations", urgency: "Academic conference dates fixed, 6–12 weeks typical notice", icon: "ri-map-pin-2-line" },
      { name: "Central America", context: "Church mission trips to Guatemala and Honduras, medical mission travel", urgency: "Mission trip dates set 2–3 months in advance by organizing churches", icon: "ri-map-pin-2-line" },
      { name: "Canada", context: "Academic conferences, healthcare professional meetings, CRW connections via hubs", urgency: "Conference dates typically fixed 4–8 weeks in advance", icon: "ri-map-pin-2-line" },
      { name: "Italy & Spain", context: "WVU Tech summer programs, healthcare conferences, European leisure travel", urgency: "Summer programs organized through WVU semester schedule", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other West Virginia Service Areas",
    crossLinksDesc: "We serve travelers across the Mountain State.",
    crossLinks: [
      { name: "Charleston", route: "/cities/charleston" },
      { name: "Huntington", route: "/cities/huntington" },
    ],
  },
};